import { Multiselect, SelectProps } from '@cloudscape-design/components';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  AUDIT_STATUS_LABELS,
  AUDIT_TYPE_LABELS,
  AuditStatus,
} from 'src/contstant/commonLabels';

interface MultiSelectAuditStatusProps {
  'selectedStatuses': string[]; // Currently selected audit status values
  'onSelectionChange': (selectedStatuses: string[]) => void; // Callback when selection changes
  'readOnly'?: boolean; // Whether the component is read-only
  'data-testid'?: string; // Test ID for testing
  'placeholder'?: string; // Placeholder text
}

/**
 * MultiSelectAuditStatus - Multi-select dropdown for audit statuses
 * Allows users to select multiple audit statuses with auto-filtering
 */
const MultiSelectAuditStatus = ({
  selectedStatuses,
  onSelectionChange,
  readOnly = false,
  'data-testid': testId = 'audit-status-multiselect',
  placeholder = 'Select audit statuses',
}: MultiSelectAuditStatusProps) => {
  // Local state for selected options in the multiselect format
  const [selectedOptions, setSelectedOptions] = useState<SelectProps.Option[]>([]);

  // Memoized options list from audit status enum
  const statusOptions = useMemo(() => {
    return Object.values(AuditStatus).map((v) => ({
      value: v,
      label: AUDIT_STATUS_LABELS[v as keyof typeof AuditStatus],
    }));
  }, []);

  // Sync local state with prop changes
  useEffect(() => {
    setSelectedOptions(
      statusOptions.filter((option) => selectedStatuses.includes(option.value)),
    );
  }, [selectedStatuses, statusOptions]);

  return (
    <Multiselect
      readOnly={readOnly}
      data-testid={testId}
      selectedOptions={selectedOptions}
      onChange={({ detail }) => {
        const values = detail.selectedOptions as SelectProps.Option[];
        // Extract values and notify parent component
        onSelectionChange(values.map((item) => item.value || ''));
        setSelectedOptions(values);
      }}
      filteringType="auto" // Enable auto-filtering of options
      options={statusOptions}
      placeholder={placeholder}
    />
  );
};

// Memoized to prevent unnecessary re-renders when props haven't changed
export default memo(MultiSelectAuditStatus);
